/**
 * LegalDocumentPage — publiczna strona wyświetlająca dokument prawny (regulamin/privacy/cookies).
 * Content w MongoDB przechowywany jako Markdown; parser prosty (nagłówki, listy, pogrubienie, tabele).
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, FileText, Shield, Cookie, ClipboardList, Trophy, Loader2 } from 'lucide-react';
import PublicFooter from './PublicFooter';

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_META = {
  regulamin:  { title: 'Regulamin',            Icon: FileText },
  privacy:    { title: 'Polityka Prywatności', Icon: Shield },
  cookies:    { title: 'Polityka Cookies',     Icon: Cookie },
  complaints: { title: 'Regulamin reklamacji', Icon: ClipboardList },
  contest:    { title: 'Regulamin konkursu',   Icon: Trophy },
};

/* Minimalny markdown parser (nagłówki #, ##, ###; listy -, |tabele|, **bold**, linki). */
const renderMarkdown = (md) => {
  if (!md) return null;
  const lines = md.split('\n');
  const out = [];
  let listBuffer = [];
  let tableBuffer = [];

  const flushList = () => {
    if (listBuffer.length) {
      out.push(<ul key={`ul-${out.length}`} style={{ margin: '8px 0 14px 22px', color: '#cbd5e1' }}>
        {listBuffer.map((li, i) => <li key={i} dangerouslySetInnerHTML={{ __html: renderInline(li) }} />)}
      </ul>);
      listBuffer = [];
    }
  };
  const flushTable = () => {
    if (tableBuffer.length >= 2) {
      const headerCells = tableBuffer[0].split('|').map(c => c.trim()).filter(Boolean);
      const bodyRows = tableBuffer.slice(2).map(row => row.split('|').map(c => c.trim()).filter(Boolean));
      out.push(
        <div key={`table-${out.length}`} style={{ overflowX: 'auto', margin: '10px 0 16px' }}>
          <table style={{
            width: '100%', borderCollapse: 'collapse', fontSize: 13,
            background: 'rgba(255,255,255,0.02)', borderRadius: 8, overflow: 'hidden',
          }}>
            <thead>
              <tr>{headerCells.map((h, i) => (
                <th key={i} style={{
                  padding: '10px 12px', textAlign: 'left', background: 'rgba(212,175,55,0.10)',
                  color: '#d4af37', fontWeight: 800, fontSize: 11, letterSpacing: 0.5, textTransform: 'uppercase',
                  borderBottom: '1px solid rgba(212,175,55,0.2)',
                }}>{h}</th>
              ))}</tr>
            </thead>
            <tbody>{bodyRows.map((row, ri) => (
              <tr key={ri}>{row.map((c, ci) => (
                <td key={ci} style={{ padding: '10px 12px', borderBottom: '1px solid rgba(255,255,255,0.05)', color: '#cbd5e1', verticalAlign: 'top' }}
                  dangerouslySetInnerHTML={{ __html: renderInline(c) }} />
              ))}</tr>
            ))}</tbody>
          </table>
        </div>
      );
    }
    tableBuffer = [];
  };

  for (const rawLine of lines) {
    const line = rawLine.replace(/\r$/, '');
    if (line.startsWith('|') && line.endsWith('|')) { flushList(); tableBuffer.push(line); continue; }
    else if (tableBuffer.length) flushTable();

    if (line.startsWith('# ')) {
      flushList();
      out.push(<h1 key={out.length} style={{ fontSize: 28, fontWeight: 900, color: '#f8fafc', margin: '10px 0 18px', letterSpacing: '-0.01em' }}>{line.slice(2)}</h1>);
    } else if (line.startsWith('## ')) {
      flushList();
      out.push(<h2 key={out.length} style={{ fontSize: 20, fontWeight: 800, color: '#d4af37', margin: '22px 0 10px' }}>{line.slice(3)}</h2>);
    } else if (line.startsWith('### ')) {
      flushList();
      out.push(<h3 key={out.length} style={{ fontSize: 16, fontWeight: 700, color: '#f8fafc', margin: '16px 0 8px' }}>{line.slice(4)}</h3>);
    } else if (line.trim() === '---') {
      flushList();
      out.push(<hr key={out.length} style={{ border: 0, borderTop: '1px dashed rgba(255,255,255,0.1)', margin: '18px 0' }} />);
    } else if (line.startsWith('- ')) {
      listBuffer.push(line.slice(2));
    } else if (line.trim() === '') {
      flushList();
      out.push(<div key={out.length} style={{ height: 8 }} />);
    } else {
      flushList();
      out.push(<p key={out.length} style={{ margin: '0 0 10px', color: '#cbd5e1', lineHeight: 1.7 }}
        dangerouslySetInnerHTML={{ __html: renderInline(line) }} />);
    }
  }
  flushList();
  flushTable();
  return out;
};

const renderInline = (text) => {
  let html = text
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong style="color:#f8fafc;font-weight:700">$1</strong>');
  html = html.replace(/`([^`]+)`/g, '<code style="background:rgba(212,175,55,0.10);padding:1px 6px;border-radius:4px;font-size:12px;color:#d4af37">$1</code>');
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noreferrer" style="color:#d4af37;text-decoration:underline">$1</a>');
  return html;
};

const LegalDocumentPage = ({ docType }) => {
  const params = useParams();
  const type = docType || params.type;
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/api/legal/documents/${type}`)
      .then((r) => { setDoc(r.data); setLoading(false); })
      .catch((e) => { setError(e?.response?.status === 404 ? 'not_found' : 'server'); setLoading(false); });
  }, [type]);

  const meta = TYPE_META[type] || { title: 'Dokument', Icon: FileText };
  const Icon = meta.Icon;

  return (
    <div style={{
      minHeight: '100vh',
      background: 'radial-gradient(1000px 500px at 50% -100px, rgba(212,175,55,0.10), transparent 60%), linear-gradient(180deg, #060b16 0%, #0b1220 55%, #060b16 100%)',
      color: '#f8fafc', display: 'flex', flexDirection: 'column',
    }}>
      <div style={{ flex: 1, padding: '40px 20px 20px' }}>
        <div style={{ maxWidth: 820, margin: '0 auto' }}>
          <button onClick={() => {
              // Wróć do strony, z której użytkownik przyszedł (np. /reklamacja/nowysacz)
              try {
                if (document.referrer) {
                  const ref = new URL(document.referrer);
                  if (ref.host === window.location.host) {
                    window.location.href = document.referrer;
                    return;
                  }
                }
              } catch { /* ignore */ }
              if (window.history.length > 1) return navigate(-1);
              navigate('/');
            }}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '8px 14px', borderRadius: 999,
              background: 'transparent', color: '#cbd5e1',
              border: '1px solid rgba(255,255,255,0.1)', cursor: 'pointer',
              fontSize: 13, fontWeight: 600, marginBottom: 24,
            }}
            data-testid="legal-doc-back">
            <ArrowLeft size={14} /> Wróć
          </button>

          <div style={{
            display: 'flex', alignItems: 'center', gap: 14, marginBottom: 8,
          }}>
            <div style={{
              width: 52, height: 52, borderRadius: 14,
              background: 'linear-gradient(135deg, rgba(212,175,55,0.22), rgba(184,134,11,0.10))',
              border: '1px solid rgba(212,175,55,0.32)',
              display: 'grid', placeItems: 'center', color: '#d4af37',
            }}>
              <Icon size={26} strokeWidth={1.6} />
            </div>
            <div>
              <div style={{ fontSize: 11, letterSpacing: 3, color: '#d4af37', fontWeight: 800, textTransform: 'uppercase' }}>
                MR JERRY · Dokument prawny
              </div>
              {doc && (
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                  Wersja {doc.version} · Opublikowano {new Date(doc.publishedAt).toLocaleDateString('pl-PL')}
                </div>
              )}
            </div>
          </div>

          <div style={{
            background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(14px)',
            border: '1px solid rgba(255,255,255,0.08)', borderRadius: 18,
            padding: 32, marginTop: 20,
          }} data-testid={`legal-doc-${type}`}>
            {loading && (
              <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>
                <Loader2 size={28} className="lg-spin" />
                <div style={{ marginTop: 10 }}>Ładowanie…</div>
              </div>
            )}
            {error && !loading && (
              <div style={{ textAlign: 'center', padding: 40, color: '#fca5a5' }}>
                {error === 'not_found' ? 'Dokument nie został jeszcze opublikowany.' : 'Błąd ładowania dokumentu.'}
              </div>
            )}
            {doc && !loading && renderMarkdown(doc.content)}
          </div>

          <div style={{
            marginTop: 20, padding: 14,
            background: 'rgba(212,175,55,0.06)', border: '1px solid rgba(212,175,55,0.18)',
            borderRadius: 12, fontSize: 12, color: '#94a3b8',
            display: 'flex', gap: 16, flexWrap: 'wrap', justifyContent: 'center',
          }}>
            <Link to="/regulamin" style={{ color: '#d4af37', textDecoration: 'none' }}>Regulamin</Link>
            <span>·</span>
            <Link to="/regulamin-reklamacji" style={{ color: '#d4af37', textDecoration: 'none' }}>Regulamin reklamacji</Link>
            <span>·</span>
            <Link to="/polityka-prywatnosci" style={{ color: '#d4af37', textDecoration: 'none' }}>Polityka prywatności</Link>
            <span>·</span>
            <Link to="/polityka-cookies" style={{ color: '#d4af37', textDecoration: 'none' }}>Polityka cookies</Link>
          </div>
        </div>
      </div>
      <PublicFooter />
      <style>{`.lg-spin{animation:lg-r 0.8s linear infinite}@keyframes lg-r{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
};

export default LegalDocumentPage;
